import React from 'react';
import { Element } from 'react-scroll';
import * as Yup from 'yup';
import {
  SubForm,
  FormSection,
  Heading,
  Box,
  FormTextInput,
  FormRadioGroup,
  RadioButton,
  FlexGrid,
  FlexGridItem,
  FormMaskedTextInput,
  FormCheckBox,
} from '@alinta-digital/asg';
import * as icon from '@alinta-digital/asg/icons/';
import { FormContainerContext } from '../../contexts/FormContainerContext';
import {
  redirectToCreditCardPaymentPortal,
  validateBsbNumber,
} from '../../components/DebitDetails/';
import { ErrorNotification } from '../../components/ErrorNotification';
import {
  setFormContextBranchDetails,
  setFormContextError,
  scrollToScrollElement,
  resetFormContextError,
} from '../../utils/utils';
import { DirectDebitForm } from '../../views/Form/Form';
import { PaymentCardType } from '@alinta-digital/payment-form/types';
import { ButtonV2, StyledLink, StyledText } from '../../Common.Styled';
import { Field, FormikProps } from 'formik';

interface DebitDetailsSubForm {
  paymentMethod: string;
  bankAccountName: string;
  bsbNumber: string;
  bankAccountNumber: string;
  accountConfirmation: boolean;
  collectDataConfirmation: boolean;
  invoiceConfirmation: boolean;
  cardType?: PaymentCardType;
  cardName?: string;
  creditCard?: {
    code: number;
    expiryMonth: string;
    expiryYear: string;
    last4: string;
    token: string;
  };
}

export interface DebitDetailsParentForm {
  debitDetails: DebitDetailsSubForm;
}

interface DebitDetailsProps {
  formikProps: FormikProps<DirectDebitForm>;
  isEdit: boolean;
  onPreviousClick: () => void;
  onNextClick: () => void;
}

export const DebitDetailsSubForm: React.FC<DebitDetailsProps> = ({
  formikProps,
  onPreviousClick,
  onNextClick,
}) => {
  const LoaderIcon = icon.Loader;
  const {
    debitDetails: debitDetailParentValues,
    identifyYourAccountForm: identifyYourAccountParentValues,
  } = formikProps.values;
  const {
    electricity: parentFormElectricity,
    gas: parentFormGas,
  } = formikProps.values.connection;

  const {
    values: formContextValues,
    dispatch: formContextDispatch,
  } = React.useContext(FormContainerContext);
  const {
    error: formContextError,
    bankBranch: formContextBankBranch,
  } = formContextValues;

  const [disableNext, setDisableNext] = React.useState(false);
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [loadingBsbField, setLoadingBsbField] = React.useState(false);
  const [typingBsbField, setTypingBsbField] = React.useState(false);
  const [bankBranch, setBankBranch] = React.useState({
    valid: formContextBankBranch.valid,
    bsbNumber: formContextBankBranch.bsbNumber,
    displayName: formContextBankBranch.displayName,
    hideDisplayName: formContextBankBranch.hideDisplayName,
  });

  React.useEffect(() => {
    if (formContextError.steps[2].status) {
      scrollToScrollElement('notificationError');
    } else {
      scrollToScrollElement('debitDetailsStart');
    }
  }, [formContextError.steps]);

  React.useEffect(() => {
    if (
      document.getElementById('debitDetails.bankAccountNumber') &&
      bankBranch.valid &&
      !loadingBsbField &&
      !typingBsbField
    )
      document.getElementById('debitDetails.bankAccountNumber')?.focus();
  }, [bankBranch.valid, loadingBsbField, typingBsbField]);

  return (
    <SubForm<DebitDetailsParentForm, DebitDetailsSubForm>
      formikProps={formikProps}
      valuesKey="debitDetails"
      config={{
        debitDetails: {
          paymentMethod: {
            validation: Yup.string().required('Please select a payment method'),
            initialValue: debitDetailParentValues.paymentMethod,
          },
          accountConfirmation: {
            validation: Yup.boolean().test(
              'validation',
              'Need to read and agree',
              (value) => value
            ),
            initialValue: debitDetailParentValues.accountConfirmation,
          },
          collectDataConfirmation: {
            validation: Yup.boolean().test(
              'validation',
              'Need to read and agree',
              (value) => value
            ),
            initialValue: debitDetailParentValues.collectDataConfirmation,
          },
          invoiceConfirmation: {
            validation: Yup.boolean().test(
              'validation',
              'Need to read and agree',
              (value) => value
            ),
            initialValue: debitDetailParentValues.invoiceConfirmation,
          },
          bankAccountName: {
            validation: Yup.string().when('paymentMethod', {
              is: 'bankTransfer',
              then: Yup.string()
                .trim()
                .required('Please enter an account name.'),
              otherwise: Yup.string(),
            }),
            initialValue: debitDetailParentValues.bankAccountName,
          },
          bsbNumber: {
            validation: Yup.string().when('paymentMethod', {
              is: 'bankTransfer',
              then: Yup.string()
                .trim()
                .required('Please enter a valid BSB Number.')
                .min(7, 'Please enter a valid BSB Number.')
                .test(
                  'bsbNumber',
                  'Please enter a valid BSB Number.',
                  (bsbNumber: string) => {
                    if (
                      bankBranch.bsbNumber === bsbNumber &&
                      !bankBranch.valid
                    ) {
                      return false;
                    }

                    return true;
                  }
                ),
              otherwise: Yup.string(),
            }),
            initialValue: debitDetailParentValues.bsbNumber,
          },
          bankAccountNumber: {
            validation: Yup.string().when('paymentMethod', {
              is: 'bankTransfer',
              then: Yup.string()
                .required('Please enter your account number.')
                .min(6, 'Please enter a 6-9 digit account number.')
                .max(9, 'Please enter a 6-9 digit account number.'),
              otherwise: Yup.string(),
            }),
            initialValue: debitDetailParentValues.bankAccountNumber,
          },
        },
      }}
    >
      {({ validateSubForm, subFormFormikProps }) => {
        const { debitDetails: debitDetailValues } = subFormFormikProps.values;
        const { debitDetails: debitDetailErrors } = subFormFormikProps.errors;
        return (
          <FormSection
            id="DebitDetails"
            heading="Step 3: Your debit details"
            stepNumber="3"
            totalSteps="4"
            previousButton={
              <ButtonV2
                id="debitDetailsPreviousButton"
                secondary={true}
                type="button"
                onClick={() => {
                  resetFormContextError(
                    formContextValues,
                    formContextDispatch,
                    2
                  );
                  onPreviousClick();
                }}
              >
                Back
              </ButtonV2>
            }
            nextButton={
              <ButtonV2
                id="debitDetailsNextButton"
                disabled={
                  !subFormFormikProps.dirty ||
                  debitDetailErrors !== undefined ||
                  (debitDetailValues.paymentMethod === 'bankTransfer' &&
                    !bankBranch.valid)
                }
                loading={submitLoading}
                primary={true}
                type="button"
                onClick={async () => {
                  setSubmitLoading(true);
                  const { isValid } = await validateSubForm();

                  if (!isValid) {
                    setSubmitLoading(false);
                    return false;
                  }

                  formikProps.setValues({
                    ...formikProps.values,
                    ...subFormFormikProps.values,
                  });

                  if (debitDetailValues.paymentMethod === 'creditCard') {
                    try {
                      const response = await redirectToCreditCardPaymentPortal(
                        identifyYourAccountParentValues.accountNumber,
                        parentFormElectricity[0]
                          ? parentFormElectricity[0].accountNumber
                          : undefined,
                        parentFormGas[0]
                          ? parentFormGas[0].accountNumber
                          : undefined
                      );

                      if (response.status === 200) {
                        window.location.href = response.data.sessionUrl;
                      } else {
                        const error = {};
                        throw error;
                      }
                    } catch (e) {
                      setSubmitLoading(false);
                      setFormContextError(
                        formContextValues,
                        formContextDispatch,
                        2,
                        true,
                        'Other'
                      );
                    }
                  } else {
                    setSubmitLoading(false);
                    setFormContextBranchDetails(
                      formContextValues,
                      formContextDispatch,
                      bankBranch.valid,
                      bankBranch.bsbNumber,
                      bankBranch.displayName,
                      bankBranch.hideDisplayName
                    );

                    onNextClick();
                  }
                }}
              >
                Next
              </ButtonV2>
            }
          >
            {formContextError.steps[2].status ? (
              <ErrorNotification
                errorType={formContextError.steps[2].type}
                stateShortName={formContextValues.stateShortName}
                onClick={() =>
                  setFormContextError(
                    formContextValues,
                    formContextDispatch,
                    2,
                    false,
                    undefined
                  )
                }
              />
            ) : (
              <>
                <Element name="debitDetailsStart" id="debitDetailsStart" />
                <Box margin={{ bottom: '24px' }}>
                  <Heading level={3} color="#000F20" weight={700}>
                    How does Direct Debit work?
                  </Heading>
                  <Box margin={{ top: 'xsmall', bottom: '24px' }}>
                    <StyledText lineHeight="21px">
                      Direct Debit lets you pay automatically from your
                      nominated cheque, savings, transaction or credit card
                      account. The amount due is debited on the due date or your
                      nominated installment date. You&apos;ll still receive a
                      bill, so you can keep track of your energy consumption,
                      along with a message confirming the automatic payment.
                    </StyledText>
                  </Box>
                  <Box margin={{ bottom: '24px' }}>
                    <Field
                      component={FormCheckBox}
                      name="debitDetails.accountConfirmation"
                      id="debitDetails.accountConfirmation"
                      data-testid="accountConfirmation"
                      label={
                        <StyledText>
                          I confirm that I am the account holder for the
                          nominated payment method, or authorised by the account
                          holder to use that account for the purposes of setting
                          up direct debit.
                        </StyledText>
                      }
                    />
                  </Box>
                  <Box margin={{ bottom: '24px' }}>
                    <Field
                      component={FormCheckBox}
                      name="debitDetails.collectDataConfirmation"
                      id="debitDetails.collectDataConfirmation"
                      data-testid="collectDataConfirmation"
                      label={
                        <StyledText>
                          I acknowledge that Alinta Energy collects my personal
                          information, and the personal information of my joint
                          account holder (if applicable), for the purpose of
                          implementing my direct debit arrangement with Alinta
                          Energy, and as otherwise described in Alinta
                          Energy&apos;s Privacy Policy, which I can access at{' '}
                          <StyledLink
                            id="privacyLink"
                            target="_blank"
                            href="https://www.alintaenergy.com.au/privacy"
                          >
                            www.alintaenergy.com.au/privacy.
                          </StyledLink>
                        </StyledText>
                      }
                    />
                  </Box>
                  <Box>
                    <Field
                      component={FormCheckBox}
                      name="debitDetails.invoiceConfirmation"
                      id="debitDetails.invoiceConfirmation"
                      data-testid="invoiceConfirmation"
                      label={
                        <StyledText>
                          I confirm that I will pay any outstanding amount as
                          per my previous preferred payment method by the due
                          date. If I fail to pay the outstanding balance, I
                          acknowledge it will be carried forward to my next bill
                          and it will be direct debited on the due date.
                        </StyledText>
                      }
                    />
                  </Box>
                </Box>
                <Box>
                  <StyledText margin={{ bottom: '10px' }}>
                    Select bank account or credit card
                  </StyledText>
                  <Field
                    id="paymentMethod"
                    name="debitDetails.paymentMethod"
                    ariaRequired={true}
                    component={FormRadioGroup}
                  >
                    <Box direction="row" gap="medium">
                      <RadioButton
                        id={'paymentMethod-bankTransfer'}
                        data-testid="bankTransferRadioButton"
                        label="Bank transfer"
                        value="bankTransfer"
                        onClick={() => {
                          if (debitDetailErrors) {
                            setDisableNext(true);
                          }
                        }}
                      />
                      <RadioButton
                        id={'paymentMethod-creditCard'}
                        data-testid="creditCardRadioButton"
                        label="Credit card"
                        value="creditCard"
                        onClick={() => {
                          if (
                            parentFormElectricity.length > 1 ||
                            parentFormGas.length > 1
                          ) {
                            setFormContextError(
                              formContextValues,
                              formContextDispatch,
                              1,
                              true,
                              'Credit card limit'
                            );
                            onPreviousClick();
                          } else {
                            setDisableNext(false);
                          }
                        }}
                      />
                    </Box>
                  </Field>
                </Box>
                {debitDetailValues.paymentMethod === 'bankTransfer' && (
                  <Box>
                    <FlexGrid direction="row">
                      <FlexGridItem md={4}>
                        <Field
                          component={FormTextInput}
                          name="debitDetails.bankAccountName"
                          id="debitDetails.bankAccountName"
                          label="Account name"
                        />
                      </FlexGridItem>
                      <FlexGridItem md={4}>
                        <Field
                          width="100%"
                          component={FormMaskedTextInput}
                          name="debitDetails.bsbNumber"
                          id="debitDetails.bsbNumber"
                          label="BSB number"
                          options={{
                            blocks: [3, 3],
                            delimiter: '-',
                            numericOnly: true,
                          }}
                          disabled={
                            debitDetailValues.bankAccountName.length === 0 ||
                            loadingBsbField
                          }
                          inputIcon={
                            loadingBsbField ? (
                              <LoaderIcon id="bsbLoader" />
                            ) : (
                              undefined
                            )
                          }
                          message={
                            !bankBranch.hideDisplayName
                              ? bankBranch.displayName
                              : undefined
                          }
                          onChange={() => {
                            if (!typingBsbField) {
                              setTypingBsbField(true);
                            }

                            if (!bankBranch.hideDisplayName) {
                              setBankBranch({
                                ...bankBranch,
                                hideDisplayName: true,
                              });
                            }

                            if (!disableNext) {
                              setDisableNext(true);
                            }
                          }}
                          onBlur={async () => {
                            setTypingBsbField(false);
                            const bsbNumber = debitDetailValues.bsbNumber;

                            if (
                              debitDetailErrors &&
                              debitDetailErrors.bsbNumber !== undefined
                            ) {
                              return false;
                            }

                            if (bankBranch.bsbNumber !== bsbNumber) {
                              setDisableNext(true);
                              setLoadingBsbField(true);
                              try {
                                const result = await validateBsbNumber(
                                  bsbNumber
                                );

                                if (result.status === 200) {
                                  setBankBranch({
                                    valid: true,
                                    bsbNumber: result.data.bsb,
                                    displayName: `${result.data.financialInstitutionCode}, ${result.data.suburb}`,
                                    hideDisplayName: false,
                                  });
                                  setLoadingBsbField(false);
                                  setDisableNext(false);
                                }
                              } catch (error) {
                                subFormFormikProps.setFieldError(
                                  'debitDetails.bsbNumber',
                                  'Please enter a valid BSB Number'
                                );
                                subFormFormikProps.setFieldTouched(
                                  'debitDetails.bsbNumber',
                                  true,
                                  false
                                );

                                setBankBranch({
                                  valid: false,
                                  bsbNumber: bsbNumber,
                                  displayName: '',
                                  hideDisplayName: true,
                                });
                                setLoadingBsbField(false);
                              }

                              return false;
                            } else if (
                              bankBranch.bsbNumber === bsbNumber &&
                              bankBranch.valid
                            ) {
                              setDisableNext(false);
                              setBankBranch({
                                ...bankBranch,
                                hideDisplayName: false,
                              });

                              return false;
                            }
                          }}
                        />
                      </FlexGridItem>
                      <FlexGridItem md={4}>
                        <Field
                          width="100%"
                          component={FormMaskedTextInput}
                          name="debitDetails.bankAccountNumber"
                          id="debitDetails.bankAccountNumber"
                          label="Account number"
                          type="tel"
                          options={{
                            numeral: true,
                            stripLeadingZeroes: false,
                            numeralThousandsGroupStyle: 'none',
                            numeralDecimalMark: 'none',
                            numeralPositiveOnly: true,
                          }}
                          disabled={
                            !bankBranch.valid ||
                            typingBsbField ||
                            loadingBsbField ||
                            (debitDetailErrors &&
                              debitDetailErrors.bsbNumber) ||
                            (debitDetailErrors &&
                              debitDetailErrors.bankAccountName)
                          }
                        />
                      </FlexGridItem>
                    </FlexGrid>
                  </Box>
                )}
              </>
            )}
          </FormSection>
        );
      }}
    </SubForm>
  );
};
